import { getCookie } from "@insite/client-framework/Common/Cookies";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import Zone from "@insite/client-framework/Components/Zone";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Page from "@insite/mobius/Page";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import { PAGE_CONTEXT } from "@store/constants";
import { GetPaymentHistoryApiParameter } from "@store/Services/PaymentHistoryService";
import { getStoreSettingsCollection } from "@store/Store/Context/ContextSelectors";
import loadPaymentHistory from "@store/Store/Data/Payment/Handlers/LoadPaymentHistory";
import { getPaymentHistoryDataView, PaymentHistoryDataViewContext } from "@store/Store/Data/Payment/PaymentsSelectors";
import updateSearchFields from "@store/Store/Pages/PaymentHistory/Handlers/UpdateSearchFields";
import StoreApplicationState from "@store/Store/StoreApplicationState";
import qs from "qs";
import * as React from "react";
import { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: StoreApplicationState) => ({
    settings: getStoreSettingsCollection(state),
    getPaymentHistoryParameter: state.store.pages.paymentHistory.getPaymentHistoryParameter,
    paymentHistoryDataView: getPaymentHistoryDataView(
        state,
        state.store.pages.paymentHistory.getPaymentHistoryParameter,
    ),
    location: getLocation(state),
});

const mapDispatchToProps = {
    loadPaymentHistory,
    updateSearchFields,
};
type Props = PageProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const PaymentHistoryPage = ({
    id,
    paymentHistoryDataView,
    getPaymentHistoryParameter,
    loadPaymentHistory,
    updateSearchFields,
    location,
    history,
    settings,
}: Props) => {
    let firstLoad = false;
    useEffect(() => {
        firstLoad = true;
        const pageSizeCookie = getCookie("PaymentHistory-PageSize");
        const pageSize = pageSizeCookie ? parseInt(pageSizeCookie, 10) : undefined;
        if (location.search) {
            const getPaymentHistoryParameter = parseQueryString<GetPaymentHistoryApiParameter>(location.search);
            if (pageSize) {
                getPaymentHistoryParameter.pageSize = pageSize;
            }
            updateSearchFields({ ...getPaymentHistoryParameter, type: "Replace" });
        } else if (settings.poolcorpSettings.paymentHistoryLookBackDays > 0) {
            const tzOffset = new Date().getTimezoneOffset() * 60000;
            const fromDate = new Date(
                Date.now() - settings.poolcorpSettings.paymentHistoryLookBackDays * 60 * 60 * 24 * 1000 - tzOffset,
            );
            const toDate = new Date(Date.now() - tzOffset);
            updateSearchFields({
                fromDate: fromDate.toISOString().split("T")[0],
                toDate: toDate.toISOString().split("T")[0],
                type: "Initialize",
                pageSize,
            });
        }
    }, []);

    useEffect(() => {
        if (!firstLoad) {
            const queryString = qs.stringify(getPaymentHistoryParameter);
            history.replace(`${location.pathname}${queryString !== "" ? `?${queryString}` : ""}`);
        }
    }, [getPaymentHistoryParameter]);

    useEffect(() => {
        if (!firstLoad) {
            if (!paymentHistoryDataView.value && !paymentHistoryDataView.isLoading) {
                loadPaymentHistory(getPaymentHistoryParameter);
            }
        }
    });

    return (
        <Page>
            <PaymentHistoryDataViewContext.Provider value={paymentHistoryDataView}>
                <Zone contentId={id} zoneName="Content" />
            </PaymentHistoryDataViewContext.Provider>
        </Page>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(PaymentHistoryPage)),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
    },
};

export default pageModule;

export const PaymentHistoryPageContext = PAGE_CONTEXT.paymentHistoryPage;
